/**
 * Three.js wireframe icosahedron + space-shell particle field, mouse-reactive,
 * sitting behind the hero title.
 */

import * as THREE from 'three';

const FLARE_COLOR = 0xff6b35;
const INNER_COLOR = 0x141414;

export function setupHeroScene() {
  const hero = document.querySelector<HTMLElement>('.s-hero');
  if (!hero) return;

  const wrap = document.createElement('div');
  wrap.className = 'fx-hero-canvas';
  hero.insertBefore(wrap, hero.firstChild);

  const w = () => wrap.clientWidth;
  const h = () => wrap.clientHeight;

  const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.setSize(w(), h());
  wrap.appendChild(renderer.domElement);

  // Compact viewports get the camera pulled back so the wireframe + star field
  // don't overwhelm the (much taller, narrower) hero on mobile.
  const isCompact = window.matchMedia('(max-width: 720px)').matches;

  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(45, w() / h(), 0.1, 100);
  camera.position.z = isCompact ? 10.5 : 6;

  // wireframe icosahedron
  const geo = new THREE.IcosahedronGeometry(2.0, 1);
  const wires = new THREE.LineSegments(
    new THREE.WireframeGeometry(geo),
    new THREE.LineBasicMaterial({ color: FLARE_COLOR, transparent: true, opacity: 0.55 }),
  );
  scene.add(wires);

  const inner = new THREE.Mesh(
    geo,
    new THREE.MeshBasicMaterial({ color: INNER_COLOR, transparent: true, opacity: 0.85 }),
  );
  inner.scale.setScalar(0.985);
  scene.add(inner);

  // dense star shell — fewer particles on mobile to keep the hero gentle on
  // battery / GPU on lower-end phones
  const pCount = isCompact ? 240 : 700;
  const positions = new Float32Array(pCount * 3);
  const basePositions = new Float32Array(pCount * 3);
  const phases = new Float32Array(pCount);
  for (let i = 0; i < pCount; i++) {
    const r = 2.8 + Math.pow(Math.random(), 0.55) * 6.5;
    const t = Math.random() * Math.PI * 2;
    const p = Math.acos(2 * Math.random() - 1);
    const x = r * Math.sin(p) * Math.cos(t);
    const y = r * Math.sin(p) * Math.sin(t);
    const z = r * Math.cos(p);
    positions[i*3+0] = basePositions[i*3+0] = x;
    positions[i*3+1] = basePositions[i*3+1] = y;
    positions[i*3+2] = basePositions[i*3+2] = z;
    phases[i] = Math.random() * Math.PI * 2;
  }
  const pGeo = new THREE.BufferGeometry();
  pGeo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  const pMat = new THREE.PointsMaterial({
    color: 0xfafafa, size: 0.022, transparent: true, opacity: 0.7, sizeAttenuation: true,
  });
  const points = new THREE.Points(pGeo, pMat);
  scene.add(points);

  // close-in flare-tinted "near stars"
  const nearCount = isCompact ? 28 : 70;
  const nPos = new Float32Array(nearCount * 3);
  for (let i = 0; i < nearCount; i++) {
    const r = 3.2 + Math.random() * 2.6;
    const t = Math.random() * Math.PI * 2;
    const p = Math.acos(2 * Math.random() - 1);
    nPos[i*3+0] = r * Math.sin(p) * Math.cos(t);
    nPos[i*3+1] = r * Math.sin(p) * Math.sin(t);
    nPos[i*3+2] = r * Math.cos(p);
  }
  const nGeo = new THREE.BufferGeometry();
  nGeo.setAttribute('position', new THREE.BufferAttribute(nPos, 3));
  const nMat = new THREE.PointsMaterial({
    color: FLARE_COLOR, size: 0.055, transparent: true, opacity: 0.9, sizeAttenuation: true,
  });
  const nearStars = new THREE.Points(nGeo, nMat);
  scene.add(nearStars);

  // mouse tracking
  let mx = 0, my = 0, mxT = 0, myT = 0;
  window.addEventListener('mousemove', (e) => {
    mxT = (e.clientX / window.innerWidth - 0.5) * 2;
    myT = (e.clientY / window.innerHeight - 0.5) * 2;
  });

  const onResize = () => {
    camera.aspect = w() / h();
    camera.updateProjectionMatrix();
    renderer.setSize(w(), h());
  };
  window.addEventListener('resize', onResize);

  // pause when off-screen so it doesn't drain GPU during scroll
  let visible = true;
  const io = new IntersectionObserver((entries) => {
    entries.forEach((e) => { visible = e.isIntersecting; });
  });
  io.observe(wrap);

  const positionAttr = pGeo.attributes.position as THREE.BufferAttribute;
  const t0 = performance.now();
  const tick = () => {
    requestAnimationFrame(tick);
    if (!visible) return;
    const t = (performance.now() - t0) / 1000;
    mx += (mxT - mx) * 0.06;
    my += (myT - my) * 0.06;

    wires.rotation.x = t * 0.12 + my * 0.5;
    wires.rotation.y = t * 0.18 + mx * 0.6;
    inner.rotation.copy(wires.rotation);

    const pulse = 1 + Math.sin(t * 1.3) * 0.02 + Math.hypot(mx, my) * 0.04;
    wires.scale.setScalar(pulse);
    inner.scale.setScalar(pulse * 0.985);

    const arr = positionAttr.array as Float32Array;
    for (let i = 0; i < pCount; i++) {
      const ph = phases[i] ?? 0;
      arr[i*3+0] = (basePositions[i*3+0] ?? 0) + Math.sin(t * 0.4 + ph) * 0.04;
      arr[i*3+1] = (basePositions[i*3+1] ?? 0) + Math.cos(t * 0.35 + ph) * 0.04;
      arr[i*3+2] = (basePositions[i*3+2] ?? 0) + Math.sin(t * 0.3 + ph * 1.3) * 0.04;
    }
    positionAttr.needsUpdate = true;
    pMat.opacity = 0.55 + Math.sin(t * 0.8) * 0.15;

    points.rotation.y = t * 0.02 + mx * 0.15;
    points.rotation.x = my * 0.1;
    nearStars.rotation.y = -t * 0.03 + mx * 0.25;
    nearStars.rotation.x = -my * 0.18;
    nMat.opacity = 0.7 + Math.sin(t * 1.2 + 1.4) * 0.2;

    renderer.render(scene, camera);
  };
  tick();
}
